import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import QRCode from 'qrcode';
import appConfig from '../config';
import '../styles/GatewayLanding.css';

// ─── Icons ────────────────────────────────────────────────────
const IcApple = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);
const IcPlay = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M3.18 23.76c.31.17.67.19 1.01.04l12.2-7.05-2.64-2.64-10.57 9.65zM.5 1.27C.19 1.6 0 2.1 0 2.73v18.54c0 .63.19 1.13.5 1.46l.07.07 10.38-10.38v-.24L.57 1.2.5 1.27zM20.34 10.3l-2.91-1.68-2.93 2.93 2.93 2.93 2.93-1.69c.84-.48.84-1.27-.02-1.49zM4.19.24L16.39 7.29l-2.64 2.64L3.18.27C3.52.12 3.88.08 4.19.24z" />
  </svg>
);
const IcArrow = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const GatewayLanding = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [city, setCity] = useState('');
  const [cityResults, setCityResults] = useState(null); // null = not yet searched
  const [cityLoading, setCityLoading] = useState(false);
  const [cityError, setCityError] = useState(null);
  const [marqueeLogos, setMarqueeLogos] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const searchTimerRef = useRef(null);

  // Lock body scroll when mobile search overlay is open
  useEffect(() => {
    if (searchFocused) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [searchFocused]);

  // Fetch group logos for marquee strip
  useEffect(() => {
    const base = appConfig.API_URL || '/api';
    fetch(`${base}/public/groups/marquee-logos`)
      .then(r => r.ok ? r.json() : null)
      .then(json => { if (json?.data?.length) setMarqueeLogos(json.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (showModal && !qrCodeUrl) {
      QRCode.toDataURL('https://yesod.us/app', { width: 220, margin: 2 })
        .then(setQrCodeUrl).catch(console.error);
    }
  }, [showModal, qrCodeUrl]);

  const openStore = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
      window.open('https://apps.apple.com/us/app/yesod/id6748472269', '_blank');
    } else if (/android/i.test(ua)) {
      window.open('https://play.google.com/store/apps/details?id=us.yesod.app', '_blank');
    } else {
      setShowModal(true);
    }
  };

  const handleDownload = () => (isMobile ? openStore() : setShowModal(true));
  const doSearch = async (q) => {
    const trimmed = q.trim();
    if (!trimmed || trimmed.length < 2) {
      setCityResults(null); setCityError(null); setCityLoading(false);
      return;
    }
    setCityLoading(true); setCityError(null);
    try {
      const base = appConfig.API_URL || '/api';
      const resp = await fetch(`${base}/public/groups/city-search?q=${encodeURIComponent(trimmed)}&limit=12`);
      if (!resp.ok) throw new Error(`Server error ${resp.status}`);
      const json = await resp.json();
      setCityResults(json.data || []);
    } catch (err) {
      console.error('Search error:', err);
      setCityError(err.message || 'Search failed');
      setCityResults([]);
    } finally {
      setCityLoading(false);
    }
  };

  // Debounced as-you-type search
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    if (!city.trim() || city.trim().length < 2) {
      setCityResults(null); setCityError(null); setCityLoading(false);
      return;
    }
    setCityLoading(true);
    searchTimerRef.current = setTimeout(() => doSearch(city), 350);
    return () => clearTimeout(searchTimerRef.current);
  }, [city]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCity = (e) => {
    e.preventDefault();
    clearTimeout(searchTimerRef.current);
    doSearch(city);
  };

  const StoreBadge = ({ href, light, children }) => (
    <a
      href={href} target="_blank" rel="noopener noreferrer"
      className={`gl-badge${light ? ' gl-badge--light' : ''}`}
      onClick={e => { if (!isMobile) { e.preventDefault(); setShowModal(true); } }}
    >{children}</a>
  );

  return (
    <div className="gl">

      {/* ════════════════════ NAV ════════════════════ */}
      <header className="gl-nav">
        <div className="gl-nav__inner">
          <img
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/WhiteLogo%20(1).png"
            alt="Yesod" className="gl-nav__logo"
          />
          <button onClick={() => navigate('/login')} className="gl-nav__login">
            Community Log In
          </button>
        </div>
      </header>

      {/* ════════════════════ HERO ════════════════════ */}
      <section className="gl-hero">
        {/* Background orbs + grid */}
        <div className="gl-hero__bg" aria-hidden="true">
          <div className="gl-orb gl-orb--a" />
          <div className="gl-orb gl-orb--b" />
          <div className="gl-orb gl-orb--c" />
          <div className="gl-grid-lines" />
        </div>

        <div className="gl-hero__inner">
          {/* Copy */}
          <div className="gl-hero__copy">
            <div className="gl-eyebrow">
              <span className="gl-eyebrow__pulse" />
              Available on iOS &amp; Android
            </div>

            <h1 className="gl-hero__h1">
              Find what's<br />
              <span className="gl-gtext">happening near you.</span>
            </h1>

            <p className="gl-hero__sub">
              Events, classes, and gatherings from every community around you —
              all in one beautifully simple feed.
            </p>

            <div className="gl-hero__actions">
              <div className="gl-hero__search-wrap">
                <form className="gl-hero__search" onSubmit={handleCity}>
                  <div className="gl-location__field">
                    <svg className="gl-location__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                      type="text" placeholder="Search by city or community name..."
                      value={city} onChange={e => setCity(e.target.value)}
                      onFocus={() => { if (window.innerWidth < 900) setSearchFocused(true); }}
                      className="gl-location__input"
                    />
                    {cityLoading && <div className="gl-hero__search-spin" />}
                  </div>
                  <button type="submit" className="gl-cta" disabled={cityLoading}>
                    {cityLoading ? 'Searching…' : <>Search <IcArrow /></>}
                  </button>
                </form>

                {/* Desktop dropdown results */}
                {!searchFocused && (cityLoading || cityResults !== null) && (
                  <div className="gl-hero__dropdown">
                    {cityError && !cityLoading && <p className="gl-cr__error" style={{padding:'1rem 1.25rem',margin:0}}>{cityError}</p>}
                    {cityResults !== null && !cityLoading && (
                      cityResults.length === 0
                        ? <p className="gl-cr__empty" style={{padding:'1.25rem',margin:0}}>No communities found for "{city}".</p>
                        : <>
                            <p className="gl-hero__dropdown-meta">{cityResults.length} communit{cityResults.length===1?'y':'ies'} matching <strong>{city}</strong></p>
                            {cityResults.map(g => {
                              const initials = g.name.split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase();
                              const counts = [];
                              if (g.event_count>0) counts.push(`${g.event_count} event${g.event_count!==1?'s':''}`);
                              if (g.class_count>0) counts.push(`${g.class_count} class${g.class_count!==1?'es':''}`);
                              if (g.minyan_count>0) counts.push(`${g.minyan_count} minyan${g.minyan_count!==1?'s':''}`);
                              return (
                                <button key={g.id} className="gl-hero__dropdown-row" onClick={openStore}>
                                  <div className="gl-soverlay__av">
                                    {g.logo_url ? <img src={g.logo_url} alt={g.name} className="gl-cr__logo"/> : <span className="gl-cr__initials">{initials}</span>}
                                  </div>
                                  <div className="gl-cr__info">
                                    <p className="gl-cr__name">{g.name}</p>
                                    {(g.city||g.state) && <p className="gl-cr__loc">{[g.city,g.state].filter(Boolean).join(', ')}</p>}
                                    {counts.length>0 && <p className="gl-cr__counts">{counts.join(' · ')}</p>}
                                  </div>
                                  <svg className="gl-cr__arr" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                                </button>
                              );
                            })}
                          </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="gl-hero__stats">
              <div className="gl-stat"><strong>200+</strong><span>Communities</span></div>
              <div className="gl-stat__sep" />
              <div className="gl-stat"><strong>100+</strong><span>Cities</span></div>
              <div className="gl-stat__sep" />
              <div className="gl-stat"><strong>500+</strong><span>Upcoming events</span></div>
            </div>

            <div className="gl-badges gl-badges--hero">
              <StoreBadge href="https://apps.apple.com/us/app/yesod/id6748472269">
                <IcApple /><span><small>Download on the</small>App Store</span>
              </StoreBadge>
              <StoreBadge href="https://play.google.com/store/apps/details?id=us.yesod.app">
                <IcPlay /><span><small>Get it on</small>Google Play</span>
              </StoreBadge>
            </div>
          </div>

          {/* Phone mockup */}
          <div className="gl-hero__visual">
            <div className="gl-phone-glow" aria-hidden="true" />
            <div className="gl-phone" role="img" aria-label="App screen preview">
              <div className="gl-phone__notch" />
              <div className="gl-phone__bar" aria-hidden="true">
                <span>9:41</span><span>●●●</span>
              </div>

              {/* ── App feed mockup ── */}
              <div className="gl-mk">

                {/* Top bar */}
                <div className="gl-mk__top">
                  <span className="gl-mk__wordmark">yesod</span>
                  <div className="gl-mk__loc">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    New York, NY
                  </div>
                  <div className="gl-mk__bell">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    <span className="gl-mk__bell-badge">5</span>
                  </div>
                </div>

                {/* Section label */}
                <div className="gl-mk__sec-label">
                  <span>Happening Soon</span>
                  <span className="gl-mk__sec-all">See all</span>
                </div>

                {/* Hero event card */}
                <div className="gl-mk__hero">
                  <div className="gl-mk__hero-orb gl-mk__hero-orb--a" aria-hidden="true" />
                  <div className="gl-mk__hero-orb gl-mk__hero-orb--b" aria-hidden="true" />
                  <div className="gl-mk__hero-orb gl-mk__hero-orb--c" aria-hidden="true" />

                  <div className="gl-mk__hero-top">
                    <span className="gl-mk__live-chip">
                      <span className="gl-mk__live-dot" aria-hidden="true" />
                      Tonight
                    </span>
                    <div className="gl-mk__hero-org">
                      <div className="gl-mk__org-av" aria-hidden="true">CN</div>
                      <span>Chabad of Naples</span>
                    </div>
                  </div>

                  <div className="gl-mk__hero-name">Shabbat<br/>Dinner</div>
                  <div className="gl-mk__hero-when">Fri, Mar 7 · 7:30 PM</div>

                  <div className="gl-mk__hero-foot">
                    <div className="gl-mk__hero-avs">
                      <div className="gl-mk__hav" style={{ background: 'linear-gradient(135deg,#f97316,#ef4444)' }} />
                      <div className="gl-mk__hav" style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)' }} />
                      <div className="gl-mk__hav" style={{ background: 'linear-gradient(135deg,#06b6d4,#0284c7)' }} />
                      <div className="gl-mk__hav gl-mk__hav--more">+29</div>
                    </div>
                    <span className="gl-mk__hero-count">32 attending</span>
                    <div className="gl-mk__hero-rsvp">RSVP →</div>
                  </div>
                </div>

                {/* Section label 2 */}
                <div className="gl-mk__sec-label gl-mk__sec-label--pad">
                  <span>Also tonight</span>
                  <span className="gl-mk__sec-all">→</span>
                </div>

                {/* Feed rows */}
                <div className="gl-mk__feed-row">
                  <div className="gl-mk__feed-av" style={{ background: 'linear-gradient(135deg,#16a34a,#065f46)' }}>YI</div>
                  <div className="gl-mk__feed-info">
                    <strong>Mincha · Maariv</strong>
                    <span>Young Israel · 6:15 PM</span>
                  </div>
                  <span className="gl-mk__tag gl-mk__tag--green">Davening</span>
                </div>

                <div className="gl-mk__feed-row">
                  <div className="gl-mk__feed-av" style={{ background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)' }}>JC</div>
                  <div className="gl-mk__feed-info">
                    <strong>Board Game Night</strong>
                    <span>JCC Manhattan · 7:00 PM</span>
                  </div>
                  <span className="gl-mk__tag gl-mk__tag--blue">Social</span>
                </div>

                <div className="gl-mk__feed-row">
                  <div className="gl-mk__feed-av" style={{ background: 'linear-gradient(135deg,#b45309,#92400e)' }}>BT</div>
                  <div className="gl-mk__feed-info">
                    <strong>Parsha Study Group</strong>
                    <span>Beis Torah · 8:00 PM</span>
                  </div>
                  <span className="gl-mk__tag gl-mk__tag--amber">Class</span>
                </div>

                {/* Bottom tab bar */}
                <div className="gl-mk__tabbar">
                  <div className="gl-mk__titem gl-mk__titem--on">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                    <div className="gl-mk__tdot" />
                  </div>
                  <div className="gl-mk__titem">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  </div>
                  <div className="gl-mk__titem">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                  </div>
                  <div className="gl-mk__titem">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating live cards */}
            <div className="gl-float gl-float--1" aria-hidden="true">
              <span className="gl-float__ico">🎉</span>
              <div><strong>Shabbat Dinner</strong><span>Tonight · 12 going</span></div>
            </div>
            <div className="gl-float gl-float--2" aria-hidden="true">
              <span className="gl-float__ico">📍</span>
              <div><strong>3 events near you</strong><span>This weekend</span></div>
            </div>
            <div className="gl-float gl-float--3" aria-hidden="true">
              <span className="gl-float__ico">🕍</span>
              <div><strong>Mincha starting soon</strong><span>In 10 minutes</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ MOBILE SEARCH OVERLAY ════════════ */}
      {searchFocused && (
        <div className="gl-soverlay">
          <div className="gl-soverlay__top">
            <div className="gl-soverlay__field">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={{flexShrink:0,color:'var(--t3)'}}>
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                autoFocus
                type="text" placeholder="City or community name..."
                value={city} onChange={e => setCity(e.target.value)}
                className="gl-soverlay__input"
              />
              {cityLoading && <div className="gl-soverlay__spin" />}
            </div>
            <button className="gl-soverlay__cancel" onClick={() => { setSearchFocused(false); setCityResults(null); setCity(''); }}>
              Cancel
            </button>
          </div>
          <div className="gl-soverlay__body">
            {(!city.trim() || city.trim().length < 2)
              ? <p className="gl-soverlay__hint">Start typing to search communities…</p>
              : cityError
                ? <p className="gl-cr__error">{cityError}</p>
                : cityResults !== null && !cityLoading && (
                    cityResults.length === 0
                      ? <p className="gl-cr__empty">No communities found for "{city}".</p>
                      : <div className="gl-soverlay__list">
                          <p className="gl-cr__meta" style={{padding:'0 1.25rem .5rem'}}>{cityResults.length} communit{cityResults.length===1?'y':'ies'} matching <strong>{city}</strong></p>
                          {cityResults.map(g => {
                            const initials = g.name.split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase();
                            const counts = [];
                            if (g.event_count>0) counts.push(`${g.event_count} event${g.event_count!==1?'s':''}`);
                            if (g.class_count>0) counts.push(`${g.class_count} class${g.class_count!==1?'es':''}`);
                            if (g.minyan_count>0) counts.push(`${g.minyan_count} minyan${g.minyan_count!==1?'s':''}`);
                            return (
                              <button key={g.id} className="gl-soverlay__row" onClick={() => { setSearchFocused(false); openStore(); }}>
                                <div className="gl-soverlay__av">
                                  {g.logo_url ? <img src={g.logo_url} alt={g.name} className="gl-cr__logo"/> : <span className="gl-cr__initials">{initials}</span>}
                                </div>
                                <div className="gl-cr__info">
                                  <p className="gl-cr__name">{g.name}</p>
                                  {(g.city||g.state) && <p className="gl-cr__loc">{[g.city,g.state].filter(Boolean).join(', ')}</p>}
                                  {counts.length>0 && <p className="gl-cr__counts">{counts.join(' · ')}</p>}
                                </div>
                                <svg className="gl-cr__arr" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                              </button>
                            );
                          })}
                        </div>
                  )
            }
          </div>
        </div>
      )}

      {/* ════════════════════ MARQUEE STRIP ════════════════════ */}}
      <div className="gl-strip" aria-hidden="true">
        <div className="gl-strip__track">
          {(marqueeLogos.length > 0
            ? [...marqueeLogos, ...marqueeLogos, ...marqueeLogos]
            : Array(24).fill(null)
          ).map((g, i) => {
            const initials = g ? g.name.split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase() : '·';
            return (
              <div key={i} className="gl-strip__logo">
                {g?.logo_url
                  ? <img src={g.logo_url} alt={g.name} className="gl-strip__logo-img" />
                  : <span className="gl-strip__logo-init">{initials}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ════════════════════ FEATURES (BENTO) ════════════════════ */}
      <section className="gl-features">
        <div className="gl-features__inner">
          <p className="gl-eyebrow gl-eyebrow--dim">Why Yesod</p>
          <h2 className="gl-section-h2">Everything your community does,<br />in one place.</h2>

          <div className="gl-bento">
            {/* Tall card */}
            <div className="gl-card gl-card--tall">
              <div className="gl-card__icon">🗓️</div>
              <h3>One unified feed</h3>
              <p>Stop checking 12 different group chats. Every event from every community you follow — right here.</p>
              <div className="gl-card__art" aria-hidden="true">
                <div className="gl-mini-row" />
                <div className="gl-mini-row gl-mini-row--sm" />
                <div className="gl-mini-row" />
              </div>
            </div>

            {/* Small top-right */}
            <div className="gl-card">
              <div className="gl-card__icon">📍</div>
              <h3>Local-first discovery</h3>
              <p>See what's happening in your neighborhood before you even search for it.</p>
            </div>

            <div className="gl-card">
              <div className="gl-card__icon">🔔</div>
              <h3>Smart reminders</h3>
              <p>Timely alerts for events you actually care about. Never get notified too late.</p>
            </div>

            {/* Wide bottom card */}
            <div className="gl-card gl-card--wide">
              <div className="gl-card__icon">🤝</div>
              <h3>Stay genuinely connected</h3>
              <p>Follow communities, RSVP, and see what your people are up to — without the noise.</p>
              <div className="gl-avatars" aria-hidden="true">
                <div className="gl-av" />
                <div className="gl-av" style={{ background: 'linear-gradient(135deg,#818cf8,#6366f1)' }} />
                <div className="gl-av" style={{ background: 'linear-gradient(135deg,#c084fc,#a855f7)' }} />
                <div className="gl-av gl-av--more">+47</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ HOW IT WORKS ════════════════════ */}
      <section className="gl-how">
        <div className="gl-how__inner">
          <p className="gl-eyebrow gl-eyebrow--dim">Simple by design</p>
          <h2 className="gl-section-h2">Up and running in seconds.</h2>
          <div className="gl-steps">
            <div className="gl-step">
              <div className="gl-step__n">01</div>
              <div className="gl-step__line" aria-hidden="true" />
              <h3>Choose your communities</h3>
              <p>Find communities near you or ones you care about. Browse by location or interest.</p>
            </div>
            <div className="gl-step">
              <div className="gl-step__n">02</div>
              <div className="gl-step__line" aria-hidden="true" />
              <h3>See what's happening</h3>
              <p>Events, classes, gatherings — pulled into one clean, scrollable feed.</p>
            </div>
            <div className="gl-step">
              <div className="gl-step__n">03</div>
              <h3>Stay in the loop</h3>
              <p>Smart reminders mean you'll never miss something important again.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ AHA MOMENT ════════════════════ */}
      <section className="gl-aha">
        <div className="gl-aha__inner">
          <div className="gl-aha__phone-wrap">
            <div className="gl-aha__glow" aria-hidden="true" />
            <div className="gl-phone gl-phone--sm">
              <div className="gl-phone__notch" />
              {/* Event detail screen mockup */}
              <div className="gl-mk gl-mk--detail">
                {/* Hero banner */}
                <div className="gl-mk__detail-hero">
                  <div className="gl-mk__detail-back">‹</div>
                  <div className="gl-mk__detail-badge">This Week</div>
                </div>
                {/* Event info */}
                <div className="gl-mk__detail-body">
                  <div className="gl-mk__detail-org">
                    <div className="gl-mk__logo gl-mk__logo--sm"><span>WS</span></div>
                    <span>West Side Shul</span>
                  </div>
                  <div className="gl-mk__detail-title">Community Shabbat Dinner</div>
                  <div className="gl-mk__detail-meta">
                    <div className="gl-mk__detail-row">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                      <span>Friday, March 7 · 6:00 PM</span>
                    </div>
                    <div className="gl-mk__detail-row">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <span>123 Main St, New York</span>
                    </div>
                  </div>
                  {/* RSVP button */}
                  <div className="gl-mk__detail-rsvp">RSVP</div>
                  {/* Attendees */}
                  <div className="gl-mk__detail-going">
                    <div className="gl-mk__detail-avs">
                      <div className="gl-mk__dav" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }} />
                      <div className="gl-mk__dav" style={{ background: 'linear-gradient(135deg,#f97316,#ef4444)' }} />
                      <div className="gl-mk__dav" style={{ background: 'linear-gradient(135deg,#14b8a6,#06b6d4)' }} />
                      <div className="gl-mk__dav gl-mk__dav--more">+24</div>
                    </div>
                    <span>27 going</span>
                  </div>
                  {/* Description lines */}
                  <div className="gl-mk__detail-desc">
                    <div className="gl-mk__desc-line" style={{ width: '100%' }} />
                    <div className="gl-mk__desc-line" style={{ width: '88%' }} />
                    <div className="gl-mk__desc-line" style={{ width: '72%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="gl-aha__copy">
            <p className="gl-eyebrow gl-eyebrow--dim">The real difference</p>
            <h2 className="gl-aha__h2">
              Your community,<br />
              not scattered across<br />
              <span className="gl-gtext">12 different apps.</span>
            </h2>
            <p className="gl-aha__sub">
              Instead of scrolling group chats, opening newsletters, and checking five different websites —
              see everything happening around you in one beautiful feed.
            </p>
            <button className="gl-cta" onClick={handleDownload}>
              Get started — it's free <IcArrow />
            </button>
          </div>
        </div>
      </section>

      {/* ════════════════════ FINAL CTA ════════════════════ */}
      <section className="gl-final">
        <div className="gl-final__bg" aria-hidden="true">
          <div className="gl-orb gl-orb--a" /><div className="gl-orb gl-orb--b" />
        </div>
        <div className="gl-final__inner">
          <h2 className="gl-final__h2">Your community<br />is waiting.</h2>
          <p className="gl-final__sub">Download the app and see what's happening near you.</p>
          <div className="gl-badges gl-badges--center">
            <StoreBadge href="https://apps.apple.com/us/app/yesod/id6748472269" light>
              <IcApple /><span><small>Download on the</small>App Store</span>
            </StoreBadge>
            <StoreBadge href="https://play.google.com/store/apps/details?id=us.yesod.app" light>
              <IcPlay /><span><small>Get it on</small>Google Play</span>
            </StoreBadge>
          </div>
        </div>
      </section>

      {/* ════════════════════ FOOTER ════════════════════ */}
      <footer className="gl-footer">
        <div className="gl-footer__inner">
          <div className="gl-footer__brand">
            <img
              src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/WhiteLogo%20(1).png"
              alt="Yesod" className="gl-footer__logo"
            />
            <span>Yesod</span>
          </div>
          <nav className="gl-footer__nav" aria-label="Footer">
            <Link to="/privacy-policy" target="_blank">Privacy</Link>
            <Link to="/terms-of-service" target="_blank">Terms</Link>
            <a href="mailto:shalom@yesod.us">Contact</a>
            <a href="https://yesod.us/about" target="_blank" rel="noopener noreferrer">About</a>
          </nav>
          <p className="gl-footer__copy">© 2026 Yesod. All rights reserved.</p>
        </div>
      </footer>

      {/* ════════════════════ MODAL ════════════════════ */}
      {showModal && (
        <div className="gl-overlay" onClick={() => setShowModal(false)}>
          <div className="gl-modal" onClick={e => e.stopPropagation()}>
            <button className="gl-modal__close" onClick={() => setShowModal(false)} aria-label="Close">✕</button>
            <img
              src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/WhiteLogo%20(1).png"
              alt="Yesod" className="gl-modal__logo"
            />
            <h2 className="gl-modal__h2">Download Yesod</h2>
            <p className="gl-modal__sub">Scan the QR code or tap a button below.</p>
            <div className="gl-modal__qr">
              {qrCodeUrl
                ? <img src={qrCodeUrl} alt="QR Code" className="gl-modal__qr-img" />
                : <div className="gl-modal__qr-spin">Generating…</div>
              }
            </div>
            <div className="gl-badges gl-badges--center">
              <StoreBadge href="https://apps.apple.com/us/app/yesod/id6748472269">
                <IcApple /><span><small>Download on the</small>App Store</span>
              </StoreBadge>
              <StoreBadge href="https://play.google.com/store/apps/details?id=us.yesod.app">
                <IcPlay /><span><small>Get it on</small>Google Play</span>
              </StoreBadge>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GatewayLanding;
